// src/components/data-sources/CombinedFileList.tsx

import React, { useState, useCallback, useRef, useMemo } from 'react';
import { Button, Spinner, Alert, Form } from 'react-bootstrap';
import { Folder, ChevronLeft } from 'lucide-react';
import { API_ENDPOINTS, GOOGLE_OAUTH_CLIENT_ID } from '../../config/api.config';
import { ListItem } from '../../types';

// Import components
import { FileListTable } from './components/FileListTable';
import DataSourceFilter from './DataSourceFilter';
import FolderDialog from './folders/FolderDialog';
import FolderSelectionModal from "../modals/data-source/FolderSelectionModal";
import FileUpload from './file-upload/FileUpload';
import GoogleDrivePicker from './google-drive/GoogleDrivePicker';
import ContextMenu from './ContextMenu.tsx';
import FolderRenameDialog from './components/FolderRenameDialog.tsx';
import DataSourceRenameDialog from './components/DataSourceRenameDialog';

// Import hooks
import { useFileOperations } from './hooks/useFileOperations';
import { useFileSelection } from './hooks/useFileSelection';
import { useDragAndDrop } from './hooks/useDragAndDrop';

// Import styles
import '../../../scss/CombinedFileList.scss';
import {apiService} from "../../services/apiService.ts";
import ContentSkeleton from "@/components/loading/ContentSkeleton.tsx";

type SourceType = 'all' | 'file_upload' | 'drive_sync';
type SortField = 'name' | 'size' | 'updated_at' | 'source_type';
type SortDirection = 'asc' | 'desc';

interface CombinedFileListProps {
    onError?: (error: string) => void;
    isAdmin?: boolean;
    refreshKey?: number;
    onUpload?: () => void;
    onDriveSync?: () => void;
    onDriveSyncSuccess?: () => void;
    showDriveSync?: boolean;
}

interface FolderDataType {
    id: string;
    name: string;
    parent_id: string | null;
}

interface Breadcrumb {
    id: string;
    name: string;
}

interface ContextMenuState {
    x: number;
    y: number;
    visible: boolean;
    itemId: string | null;
    itemName: string | null;
    itemType: 'folder' | 'file';
}

const CombinedFileList: React.FC<CombinedFileListProps> = ({
                                                               onError,
                                                               isAdmin = false,
                                                               refreshKey = 0,
                                                               onUpload,
                                                               onDriveSync,
                                                               onDriveSyncSuccess,
                                                               showDriveSync: initialShowDriveSync = false
                                                           }) => {
    // States
    const [isRenaming, setIsRenaming] = useState(false);
    const [folderToRename, setFolderToRename] = useState<{id: string, name: string} | null>(null);
    const [isRenamingDataSource, setIsRenamingDataSource] = useState(false);
    const [dataSourceToRename, setDataSourceToRename] = useState<{id: string, name: string} | null>(null);
    const [showDriveSync, setShowDriveSync] = useState(initialShowDriveSync);
    const [clientId] = useState(GOOGLE_OAUTH_CLIENT_ID);
    const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [source, setSource] = useState<SourceType>('all');
    const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false);
    const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
    const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>([]);
    const [sort, setSort] = useState<{ field: SortField; direction: SortDirection }>({
        field: 'updated_at',
        direction: 'desc'
    });
    const [contextMenu, setContextMenu] = useState<ContextMenuState>({
        x: 0,
        y: 0,
        visible: false,
        itemId: null,      // Changed from folderId
        itemName: null,    // Changed from folderName
        itemType: 'folder' // Added default value
    });
    const [showFileUpload, setShowFileUpload] = useState(false);
    const [isFolderCreating, setIsFolderCreating] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [rootFolderId, setRootFolderId] = useState<string | null>(null);
    const [isRootInitialized, setIsRootInitialized] = useState(false);

    // Custom hooks
    const {
        files,
        folders,
        isLoading,
        isInitialLoad,
        error,
        uploadStatus,
        fetchFiles,
        fetchFolders,
        fetchRootFolder,
        handleUpload: handleFileUpload,
        deleteFiles,
        moveFiles,
        handleDeleteItem
    } = useFileOperations(onError);

    const {
        selectedFiles,
        handleSelect: fileSelectionHandler,
        handleSelectAll,
        clearSelection
    } = useFileSelection();

    const combinedItems = useMemo<ListItem[]>(() => {
        const folderItems: ListItem[] = folders.map(folder => {
            // Debug log to verify incoming data
            console.log('Processing folder:', folder.name, {
                total_file_count: folder.total_file_count,
                direct_file_count: folder.direct_file_count,
                file_count: folder.file_count
            });

            return {
                ...folder,
                type: 'folder' as const,
                uniqueId: `folder-${folder.id}`,
                is_root: folder.is_root,
                file_count: parseInt(String(folder.total_file_count || folder.file_count || 0)),
                folder_metadata: {
                    file_count: parseInt(String(folder.total_file_count || folder.file_count || 0)),
                    total_file_count: parseInt(String(folder.total_file_count || folder.file_count || 0)),
                    direct_file_count: parseInt(String(folder.direct_file_count || 0)),
                    child_count: parseInt(String(folder.direct_subfolder_count || 0)),
                    total_subfolder_count: parseInt(String(folder.total_subfolder_count || 0)),
                    subfolder_count: parseInt(String(folder.direct_subfolder_count || 0)),
                    recursive_size: folder.total_size,
                    total_size: folder.total_size,
                    depth: folder.depth,
                    path_tokens: folder.path_tokens,
                    s3_prefix: folder.s3_prefix
                },
                metadata: {
                    file_size: folder.total_size,
                    mime_type: undefined,
                    object_name: undefined,
                    original_filename: undefined,
                    upload_completed_at: undefined,
                    description: folder.description,
                    folder_prefix: folder.s3_prefix,
                    path_tokens: folder.path_tokens,
                    full_path: folder.full_path,
                    file_count: parseInt(String(folder.total_file_count || folder.file_count || 0)),
                    direct_file_count: parseInt(String(folder.direct_file_count || 0)),
                    total_subfolder_count: parseInt(String(folder.total_subfolder_count || 0)),
                    child_count: parseInt(String(folder.direct_subfolder_count || 0))
                }
            };
        });

        const fileItems: ListItem[] = files.map(file => ({
            ...file,
            type: 'file' as const,
            uniqueId: `file-${file.id}`,
            metadata: {
                mime_type: file.metadata?.mime_type,
                file_size: file.metadata?.file_size,
                original_filename: file.metadata?.original_filename,
                object_name: file.metadata?.object_name,
                upload_completed_at: file.metadata?.upload_completed_at,
                description: file.metadata?.description,
                source: file.metadata?.source,
                upload_started_at: file.metadata?.upload_started_at,
                sync_started_at: file.metadata?.sync_started_at,
                sync_completed_at: file.metadata?.sync_completed_at,
                drive_file_id: file.metadata?.drive_file_id,
                folder_prefix: file.metadata?.folder_prefix,
                path_tokens: file.metadata?.path_tokens,
                full_path: file.path
            }
        }));

        return [...folderItems, ...fileItems];
    }, [folders, files]);

    const moveOperations = useMemo(() => ({
        moveItem: async (sourceId: string, targetId: string | null): Promise<Response> => {
            const item = combinedItems.find(i => i.id === sourceId);
            const endpoint = item?.type === 'folder'
                ? API_ENDPOINTS.DATA_SOURCE.MOVE_FOLDER
                : API_ENDPOINTS.DATA_SOURCE.MOVE_TO_FOLDER;

            return apiService['fetchWithAuth'](endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    sourceId,
                    folderId: targetId
                })
            });
        },

        moveBulkItems: async (itemIds: string[], targetId: string | null): Promise<Response[]> => {
            return Promise.all(itemIds.map(itemId => {
                const item = combinedItems.find(i => i.id === itemId);
                const endpoint = item?.type === 'folder'
                    ? API_ENDPOINTS.DATA_SOURCE.MOVE_FOLDER
                    : API_ENDPOINTS.DATA_SOURCE.MOVE_TO_FOLDER;

                return apiService['fetchWithAuth'](endpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        sourceId: itemId,
                        folderId: targetId
                    })
                });
            }));
        },
        beforeMove: async () => {
            await fetchFolders(null);
        },
        afterMove: async () => {
            if (currentFolderId) {
                await fetchFolders(currentFolderId);
            }
            await fetchFiles({
                currentPage,
                itemsPerPage,
                searchTerm,
                source,
                sort,
                currentFolderId,
                rootFolderId
            });
        }
    }), [combinedItems, currentFolderId, currentPage, fetchFiles, fetchFolders, itemsPerPage, rootFolderId, searchTerm, sort, source]);

    const {
        dragInfo,
        handleDragStart,
        handleDragOver,
        handleDrop,
        handleDragEnd,  // Add this
        resetDragState
    } = useDragAndDrop(
        moveOperations,
        () => {
            clearSelection();
            resetDragState();
        },
        onError
    );

    React.useEffect(() => {
        const initializeAndLoadData = async () => {
            try {
                if (!isRootInitialized) {
                    const rootId = await fetchRootFolder();
                    if (rootId) {
                        setRootFolderId(rootId);
                        setCurrentFolderId(rootId);
                        setIsRootInitialized(true);
                    }
                }

                const targetFolderId = currentFolderId || rootFolderId;
                if (!targetFolderId) return;

                clearSelection();
                await fetchFolders(targetFolderId);
                const { totalPages: pages } = await fetchFiles({
                    currentPage,
                    itemsPerPage,
                    searchTerm,
                    source,
                    sort,
                    currentFolderId: targetFolderId,
                    rootFolderId
                });
                setTotalPages(pages);
            } catch (error) {
                console.error('Error loading data:', error);
                onError?.('Failed to load folder contents');
            }
        };

        void initializeAndLoadData();
    }, [
        currentPage,
        itemsPerPage,
        source,
        searchTerm,
        refreshKey,
        sort,
        currentFolderId,
        rootFolderId,
        isRootInitialized,
        clearSelection,
        fetchFiles,
        fetchFolders,
        fetchRootFolder,
        onError
    ]);

    // Handlers
    const handleDriveSync = useCallback(() => {
        setShowDriveSync(true);
        onDriveSync?.();
    }, [onDriveSync]);

    const handleDriveSyncError = useCallback((error: string) => {
        onError?.(error);
    }, [onError]);

    const handleDriveSyncSuccess = useCallback(() => {
        setShowDriveSync(false);
        void fetchFiles({
            currentPage,
            itemsPerPage,
            searchTerm,
            source,
            sort,
            currentFolderId
        });
        onDriveSyncSuccess?.();
    }, [currentFolderId, currentPage, fetchFiles, itemsPerPage, onDriveSyncSuccess, searchTerm, sort, source]);

    const handleUpload = useCallback(() => {
        console.log('Initiating upload for folder:', currentFolderId); // Add logging
        fileInputRef.current?.click();
    }, [currentFolderId]);

    const handleUploadSuccess = useCallback(async () => {
        setShowFileUpload(false);
        await fetchFolders(currentFolderId);
        await fetchFiles({
            currentPage,
            itemsPerPage,
            searchTerm,
            source,
            sort,
            currentFolderId,
            rootFolderId
        });
        onUpload?.();
    }, [currentFolderId, currentPage, fetchFiles, fetchFolders, itemsPerPage, onUpload, rootFolderId, searchTerm, sort, source]);

// Update the FileUpload modal section
    {showFileUpload && (
        <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1050 }}
            onClick={() => setShowFileUpload(false)}
        >
            <div
                className="bg-white p-4 rounded-3 shadow-lg"
                style={{ maxWidth: '500px', width: '90%' }}
                onClick={e => e.stopPropagation()}
            >
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Upload Files</h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowFileUpload(false)}
                    />
                </div>
                <FileUpload
                    onUploadSuccess={handleUploadSuccess}
                    acceptedFileTypes=".txt,.pdf,.docx,.md,.csv,.json,.xlsx,.xls,.pptx,.ppt"
                    maxFileSize={50 * 1024 * 1024}
                    currentFolderId={currentFolderId}
                    fetchFiles={fetchFiles}
                    fetchFolders={fetchFolders}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    searchTerm={searchTerm}
                    source={source as SourceType}  // Add type assertion
                    sort={sort}
                    rootFolderId={rootFolderId}
                />
            </div>
        </div>
    )}

    const handleContextMenu = useCallback((
        e: React.MouseEvent<HTMLDivElement>,
        id: string,
        name: string,
        type: 'folder' | 'file'
    ) => {
        e.preventDefault();
        e.stopPropagation();
        setContextMenu({
            x: e.pageX,
            y: e.pageY,
            visible: true,
            itemId: id,
            itemName: name,
            itemType: type
        });
    }, []);

    const handleCreateFolder = useCallback(async (folderName: string, description: string): Promise<void> => {
        if (isFolderCreating) return;

        setIsFolderCreating(true);
        try {
            await apiService.post(API_ENDPOINTS.DATA_SOURCE.CREATE_FOLDER, {
                name: folderName,
                description,
                parent_id: currentFolderId
            });

            // Refresh both folders and files
            await Promise.all([
                fetchFolders(currentFolderId),
                fetchFiles({
                    currentPage,
                    itemsPerPage,
                    searchTerm,
                    source,
                    sort,
                    currentFolderId,
                    rootFolderId
                })
            ]);

            setIsFolderDialogOpen(false);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to create folder';
            onError?.(errorMessage);
        } finally {
            setIsFolderCreating(false);
        }
    }, [
        currentFolderId,
        currentPage,
        fetchFiles,
        fetchFolders,
        isFolderCreating,
        itemsPerPage,
        onError,
        rootFolderId,
        searchTerm,
        sort,
        source
    ]);

    const handleSort = useCallback((field: SortField) => {
        setSort(prevSort => ({
            field,
            direction: prevSort.field === field && prevSort.direction === 'asc' ? 'desc' : 'asc'
        }));
    }, []);

    const handleFolderClick = useCallback((folderId: string, folderName: string) => {
        // Don't add to breadcrumbs if it's the root folder
        if (folderId !== rootFolderId) {
            setBreadcrumbs(prev => [...prev, { id: folderId, name: folderName }]);
        }
        setCurrentFolderId(folderId);
        setCurrentPage(1);
        clearSelection();
    }, [rootFolderId, clearSelection]);

    const handleNavigateToFolder = useCallback((index: number) => {
        const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
        const targetFolder = index === -1 ? null : newBreadcrumbs[newBreadcrumbs.length - 1].id;
        setCurrentFolderId(targetFolder);
        setBreadcrumbs(newBreadcrumbs);
        setCurrentPage(1);
        clearSelection();
    }, [breadcrumbs, clearSelection]);

    const handleSelect = useCallback((
        itemId: string,
        event: React.MouseEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>
    ) => {
        fileSelectionHandler(itemId, event, combinedItems);
    }, [fileSelectionHandler, combinedItems]);

    const handleBulkDelete = useCallback(async (): Promise<void> => {
        if (selectedFiles.size === 0) return;

        // Count selected files and folders
        const selectedItems = Array.from(selectedFiles).map(id =>
            combinedItems.find(item => item.id === id)
        ).filter(Boolean);

        const fileCount = selectedItems.filter(item => item?.type === 'file').length;
        const folderCount = selectedItems.filter(item => item?.type === 'folder').length;

        const confirmMessage = `Are you sure you want to delete ${fileCount} file${fileCount !== 1 ? 's' : ''}`
            + (folderCount > 0 ? ` and ${folderCount} folder${folderCount !== 1 ? 's' : ''}` : '')
            + '?';

        if (!window.confirm(confirmMessage)) return;

        const success = await deleteFiles(Array.from(selectedFiles));
        if (success) {
            clearSelection();
            await Promise.all([
                fetchFiles({
                    currentPage,
                    itemsPerPage,
                    searchTerm,
                    source,
                    sort,
                    currentFolderId
                }),
                fetchFolders(currentFolderId)
            ]);
        }
    }, [selectedFiles, deleteFiles, clearSelection, fetchFiles, currentPage, itemsPerPage, searchTerm, source, sort, currentFolderId, fetchFolders, combinedItems]);

    // In CombinedFileList.tsx, update the handleContextMenuDelete function:

    const handleContextMenuDelete = useCallback(async () => {
        console.log('Context menu delete triggered with:', {
            itemId: contextMenu.itemId,
            itemType: contextMenu.itemType
        });

        if (!contextMenu.itemId || !contextMenu.itemType) {
            console.log('Missing required delete parameters');
            return;
        }

        try {
            const result = await handleDeleteItem(contextMenu.itemId, contextMenu.itemType);
            console.log('Delete result:', result);

            if (!result.success) {
                onError?.(result.message);
            } else {
                // Refresh the view after successful deletion
                await Promise.all([
                    fetchFiles({
                        currentPage,
                        itemsPerPage,
                        searchTerm,
                        source,
                        sort,
                        currentFolderId
                    }),
                    fetchFolders(currentFolderId)
                ]);
            }

            setContextMenu(prev => ({ ...prev, visible: false }));
        } catch (error) {
            console.error('Error in handleContextMenuDelete:', error);
            onError?.(error instanceof Error ? error.message : 'Failed to delete item');
        }
    }, [
        contextMenu.itemId,
        contextMenu.itemType,
        currentFolderId,
        currentPage,
        fetchFiles,
        fetchFolders,
        handleDeleteItem,
        itemsPerPage,
        onError,
        searchTerm,
        sort,
        source
    ]);


    if (isInitialLoad) {
        return <ContentSkeleton />;
    }

    return (
        <div className="combined-file-list">
            {error && (
                <Alert variant="danger" className="mb-3">
                    {error}
                </Alert>
            )}

            {/* Breadcrumb Navigation */}
            <div className="d-flex align-items-center mb-3">
                {currentFolderId && currentFolderId !== rootFolderId && (
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleNavigateToFolder(breadcrumbs.length - 2)}
                    >
                        <ChevronLeft size={16}/>
                        Back
                    </Button>
                )}
                <nav aria-label="breadcrumb" className="flex-grow-1">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item">
                            <a href="#" onClick={(e) => {
                                e.preventDefault();
                                handleNavigateToFolder(-1);
                            }}>
                                Home
                            </a>
                        </li>
                        {breadcrumbs.map((crumb, index) => (
                            <li
                                key={`${crumb.id}-${index}`}
                                className={`breadcrumb-item ${index === breadcrumbs.length - 1 ? 'active' : ''}`}
                            >
                                {index === breadcrumbs.length - 1 ? (
                                    crumb.name
                                ) : (
                                    <a
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleNavigateToFolder(index);
                                        }}
                                    >
                                        {crumb.name}
                                    </a>
                                )}
                            </li>
                        ))}
                    </ol>
                </nav>
            </div>

            {/* Actions Bar */}
            <div className="d-flex align-items-end mb-3">
                <DataSourceFilter
                    source={source}
                    searchTerm={searchTerm}
                    onSourceChange={setSource}
                    onSearchChange={setSearchTerm}
                    onSearch={setSearchTerm}
                    onUpload={handleUpload}
                    onDriveSync={handleDriveSync}
                    onCreateFolder={() => setIsFolderDialogOpen(true)}
                    disabled={isLoading}
                />

                {isAdmin && selectedFiles.size > 0 && (
                    <div className="ms-3 d-flex gap-2">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setIsMoveDialogOpen(true)}
                            disabled={isLoading}
                            style={{height: '38px'}}
                            className="d-flex align-items-center"
                        >
                            <Folder size={16} className="me-2"/>
                            Move Items ({selectedFiles.size})
                        </Button>
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={handleBulkDelete}
                            disabled={isLoading}
                            style={{height: '38px'}}
                            className="d-flex align-items-center"
                        >
                            Delete ({selectedFiles.size})
                        </Button>
                    </div>
                )}
            </div>

            {/* File List Table */}
            <FileListTable
                items={combinedItems}
                selectedFiles={selectedFiles}
                isLoading={isLoading}
                isAdmin={isAdmin}
                dragInfo={dragInfo}
                sort={sort}
                onSort={handleSort}
                onSelect={handleSelect}
                onSelectAll={() => handleSelectAll(combinedItems)}
                onDragStart={(e, itemId) => handleDragStart(e, itemId, 'file')}
                onDragOver={(e, itemId, itemType) => handleDragOver(e, itemId, itemType)}
                onDrop={(e, targetId, targetType, selectedFiles) => handleDrop(e, targetId, targetType, selectedFiles)}
                onDragEnd={handleDragEnd}
                onFolderClick={handleFolderClick}
                onContextMenu={(e, id, name, type) => {
                    console.log('Context menu triggered for:', {id, name, type});
                    handleContextMenu(e, id, name, type);
                }}
            />

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <span className="text-muted">Items per page:</span>
                        <Form.Select
                            size="sm"
                            value={itemsPerPage}
                            onChange={(e) => setItemsPerPage(Number(e.target.value))}
                            style={{width: 'auto', minWidth: '70px'}}
                            className="py-1 pe-8"
                        >
                            {[10, 25, 50, 100].map(size => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </Form.Select>
                    </div>
                    <div className="text-muted">
                        Showing {files.length + folders.length} of {totalPages * itemsPerPage} items
                    </div>
                </div>
                <nav aria-label="Pagination" className="mt-3">
                    <ul className="pagination mb-0 justify-content-end">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1 || isLoading}
                            >
                                Previous
                            </button>
                        </li>
                        {/* First page */}
                        {currentPage > 2 && (
                            <>
                                <li className="page-item">
                                    <button className="page-link" onClick={() => setCurrentPage(1)}>
                                        1
                                    </button>
                                </li>
                                {currentPage > 3 && (
                                    <li className="page-item disabled">
                                        <span className="page-link">...</span>
                                    </li>
                                )}
                            </>
                        )}

                        {/* Current page (and surrounding pages) */}
                        {Array.from({length: 3}, (_, i) => currentPage - 1 + i)
                            .filter(page => page > 0 && page <= totalPages)
                            .map(page => (
                                <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(page)}
                                    >
                                        {page}
                                    </button>
                                </li>
                            ))
                        }

                        {/* Last page */}
                        {currentPage < totalPages - 1 && (
                            <>
                                {currentPage < totalPages - 2 && (
                                    <li className="page-item disabled">
                                        <span className="page-link">...</span>
                                    </li>
                                )}
                                <li className="page-item">
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(totalPages)}
                                    >
                                        {totalPages}
                                    </button>
                                </li>
                            </>
                        )}

                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages || isLoading}
                            >
                                Next
                            </button>
                        </li>
                    </ul>
                </nav>
            </div>

            {/* Modals */}
            <FolderDialog
                isOpen={isFolderDialogOpen}
                onClose={() => {
                    setIsFolderDialogOpen(false);
                    setIsFolderCreating(false);
                }}
                onSubmit={handleCreateFolder}
            />


            <FolderSelectionModal
                isOpen={isMoveDialogOpen}
                onClose={() => setIsMoveDialogOpen(false)}
                onMoveToFolder={async (targetFolderId) => {
                    try {
                        await moveFiles(
                            Array.from(selectedFiles),
                            targetFolderId,
                            {
                                rootFolderId,
                                currentFolderId,
                                currentPage,
                                itemsPerPage,
                                searchTerm,
                                source,
                                sort
                            }
                        );

                        clearSelection();
                        setIsMoveDialogOpen(false);

                        interface FolderHierarchyResponse {
                            folders: FolderDataType[];
                        }

                        const data = await apiService.get<FolderHierarchyResponse>(
                            API_ENDPOINTS.DATA_SOURCE.LIST_FOLDERS_HIERARCHY
                        );

                        console.log('Raw API Response:', data);

                        const targetFolder = data.folders.find((f: FolderDataType) => f.id === targetFolderId);

                        if (targetFolderId === null || targetFolderId === rootFolderId) {
                            setBreadcrumbs([]);
                            setCurrentFolderId(rootFolderId);
                        } else if (targetFolder) {
                            const path: { id: string; name: string }[] = [];
                            let current: FolderDataType | undefined = targetFolder;

                            while (current && current.id !== rootFolderId) {
                                path.unshift({id: current.id, name: current.name});
                                const parentFolder = data.folders.find(
                                    (f: FolderDataType) => f.id === current?.parent_id
                                );
                                if (!parentFolder) break;
                                current = parentFolder;
                            }

                            setBreadcrumbs(path);
                            setCurrentFolderId(targetFolderId);
                        }

                        setCurrentPage(1);
                        await fetchFiles({
                            currentPage: 1,
                            itemsPerPage,
                            searchTerm,
                            source,
                            sort,
                            currentFolderId: targetFolderId,
                            rootFolderId
                        });
                    } catch (error) {
                        console.error('Error moving files:', error);
                        onError?.(error instanceof Error ? error.message : 'Failed to move files');
                    }
                }}
                currentFolderId={currentFolderId}
                sourceFolderId={currentFolderId || undefined}
            />

            <ContextMenu
                x={contextMenu.x}
                y={contextMenu.y}
                visible={contextMenu.visible}
                itemType={contextMenu.itemType || 'file'} // Provide a default value since itemType is now required
                onClose={() => {
                    setContextMenu(prev => ({...prev, visible: false}));
                    setIsRenamingDataSource(false);
                    setIsRenaming(false);
                }}
                onRename={() => {
                    if (contextMenu.itemId && contextMenu.itemName) {
                        if (contextMenu.itemType === 'folder') {
                            setFolderToRename({
                                id: contextMenu.itemId,
                                name: contextMenu.itemName
                            });
                            setIsRenaming(true);
                        } else {
                            setDataSourceToRename({
                                id: contextMenu.itemId,
                                name: contextMenu.itemName
                            });
                            setIsRenamingDataSource(true);
                        }
                        setContextMenu(prev => ({...prev, visible: false}));
                    }
                }}
                onDelete={handleContextMenuDelete}
            />

            {/* File Upload Modal */}
            {showFileUpload && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1050}}
                    onClick={() => setShowFileUpload(false)}
                >
                    <div
                        className="bg-white p-4 rounded-3 shadow-lg"
                        style={{maxWidth: '500px', width: '90%'}}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Upload Files</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => setShowFileUpload(false)}
                            />
                        </div>
                        <FileUpload
                            onUploadSuccess={handleUploadSuccess}
                            acceptedFileTypes=".txt,.pdf,.docx,.md,.csv,.json,.xlsx,.xls,.pptx,.ppt"
                            maxFileSize={50 * 1024 * 1024}
                            currentFolderId={currentFolderId}
                            fetchFiles={fetchFiles}
                            fetchFolders={fetchFolders}
                            currentPage={currentPage}
                            itemsPerPage={itemsPerPage}
                            searchTerm={searchTerm}
                            source={source}
                            sort={sort}
                            rootFolderId={rootFolderId}
                        />
                    </div>
                </div>
            )}


            {/* Google Drive Sync Modal */}
            {(showDriveSync && clientId) && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1050}}
                    onClick={() => setShowDriveSync(false)}
                >
                    <div
                        className="bg-white p-4 rounded-3 shadow-lg"
                        style={{maxWidth: '500px', width: '90%'}}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Google Drive Sync</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={() => setShowDriveSync(false)}
                            />
                        </div>
                        <GoogleDrivePicker
                            clientId={clientId}
                            onUploadSuccess={handleDriveSyncSuccess}
                            onError={handleDriveSyncError}
                            onFilesLoad={() => {
                            }}
                            onLoadingChange={() => {
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Upload Progress Modal */}
            {uploadStatus.isUploading && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1050}}
                >
                    <div className="bg-white p-4 rounded-3 shadow-lg text-center">
                        <Spinner animation="border" variant="primary" className="mb-3"/>
                        <div className="mb-2">
                            Uploading files ({uploadStatus.currentFile} of {uploadStatus.totalFiles})
                        </div>
                        <div className="small text-muted">
                            Current file: {uploadStatus.currentFileName}
                        </div>
                    </div>
                </div>
            )}

            {/* Rename Dialog */}
            {folderToRename && (
                <FolderRenameDialog
                    isOpen={isRenaming}
                    initialName={folderToRename?.name ?? ''}
                    onClose={() => {
                        setIsRenaming(false);
                        setFolderToRename(null);
                    }}
                    onRename={async (newName) => {
                        try {
                            await apiService.patch(
                                API_ENDPOINTS.DATA_SOURCE.RENAME_FOLDER.replace(':id', folderToRename?.id ?? ''),
                                {
                                    name: newName
                                }
                            );

                            await fetchFolders(currentFolderId);
                            setIsRenaming(false);
                            setFolderToRename(null);
                        } catch (error) {
                            console.error('Error renaming folder:', error);
                            onError?.(error instanceof Error ? error.message : 'Failed to rename folder');
                        }
                    }}
                />
            )}
            {/* Data Source Rename Dialog */}
            {dataSourceToRename && (
                <DataSourceRenameDialog
                    isOpen={isRenamingDataSource}
                    initialName={dataSourceToRename?.name ?? ''}
                    onClose={() => {
                        setIsRenamingDataSource(false);
                        setDataSourceToRename(null);
                    }}
                    onRename={async (newName) => {
                        try {
                            await apiService.patch(
                                `${API_ENDPOINTS.DATA_SOURCE.RENAME}/${dataSourceToRename?.id}`,
                                {
                                    name: newName
                                }
                            );

                            await fetchFiles({
                                currentPage,
                                itemsPerPage,
                                searchTerm,
                                source,
                                sort,
                                currentFolderId
                            });

                            setIsRenamingDataSource(false);
                            setDataSourceToRename(null);
                        } catch (error) {
                            console.error('Error renaming data source:', error);
                            onError?.(error instanceof Error ? error.message : 'Failed to rename data source');
                        }
                    }}
                />
            )}
            {/* Hidden file input for uploads */}
            <input
                type="file"
                ref={fileInputRef}
                style={{display: 'none'}}
                onChange={async (e) => {  // Make this async
                    const files = e.target.files;
                    if (files && files.length > 0) {
                        console.log('Uploading files to folder:', currentFolderId);
                        try {
                            await handleFileUpload(Array.from(files), currentFolderId);
                            // After successful upload, refresh the lists
                            await Promise.all([
                                fetchFolders(currentFolderId),
                                fetchFiles({
                                    currentPage,
                                    itemsPerPage,
                                    searchTerm,
                                    source,
                                    sort,
                                    currentFolderId,
                                    rootFolderId
                                })
                            ]);
                            onUpload?.();
                        } catch (error) {
                            console.error('Upload error:', error);
                            onError?.(error instanceof Error ? error.message : 'Failed to upload files');
                        }
                        e.target.value = '';  // Reset input
                    }
                }}
                accept=".txt,.pdf,.docx,.md,.csv,.json,.xlsx,.xls,.pptx,.ppt"
                multiple
            />
        </div>
    );
};

export default CombinedFileList;